import React, { useEffect, useState, useRef } from 'react';
import { Box, Paper, Divider, Typography, Popover, MenuItem, Dialog, DialogTitle, DialogContent, IconButton, Alert } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import epochToDate from '../functions/epochdate';
import api from '../functions/api';
import MarketAnalysisModal from './MarketAnalysisModal';
import InfoTooltip from './InfoTooltip';
import SectorOverview from './SectorOverview';
import InstitutionalRegionOverview from './InstitutionalRegionOverview';
import BroadPicture from './BroadPicture';

import Chart_Small_MUI from './charts/Chart_Small_MUI';
import fetchHistoricalData from '../functions/fetchHistoricalData';
import fetchHistoricalDataBulk from '../functions/fetchHistoricalDataBulk';

const getDateDaysAgo = (days) =>
  new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

const today = new Date().toISOString().split('T')[0];
const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

// Top 4x4 group: 16 indicators
const tickers = [
  { key: 'sp500', title: 'S&P 500', ticker: '^GSPC' },
  { key: 'nasdaq', title: 'Nasdaq', ticker: '^IXIC' },
  { key: 'dow', title: 'Dow 30', ticker: '^DJI' },
  { key: 'russell', title: 'Russell 2000', ticker: '^RUT' },
  { key: 'ftse', title: 'FTSE 100', ticker: '^FTSE' },
  { key: 'gold', title: 'Gold', ticker: 'GC=F' },
  { key: 'brent', title: 'Brent Oil', ticker: 'BZ=F' },
  { key: 'bitcoin', title: 'Bitcoin', ticker: 'BTC-USD' },
  { key: 'eurusd', title: 'EUR/USD', ticker: 'EURUSD=X' },
  { key: 'vix', title: 'VIX', ticker: '^VIX' },
];

// Bottom 4x4 group: 16 top stocks
const tickers2 = [
  { key: 'tesla', title: 'Tesla', ticker: 'TSLA' },
  { key: 'msft', title: 'MSFT', ticker: 'MSFT' },
  { key: 'aapl', title: 'Apple', ticker: 'AAPL' },
  { key: 'meta', title: 'Meta', ticker: 'META' },
  { key: 'google', title: 'Alphabet', ticker: 'GOOG' },
  { key: 'nvidia', title: 'NVIDIA', ticker: 'NVDA' },
  { key: 'amzn', title: 'Amazon', ticker: 'AMZN' },
  { key: 'intel', title: 'Intel', ticker: 'INTC' },
  { key: 'ibm', title: 'IBM', ticker: 'IBM' },
  { key: 'orcl', title: 'Oracle', ticker: 'ORCL' },
  { key: 'sap', title: 'SAP', ticker: 'SAP' },
  { key: 'baba', title: 'Alibaba', ticker: 'BABA' },
  { key: 'tsmc', title: 'TSMC', ticker: 'TSM' },
  { key: 'adbe', title: 'Adobe', ticker: 'ADBE' },
  { key: 'nvax', title: 'Novavax', ticker: 'NVAX' },
];

const useWidgetData = (list, date_start, date_end) => {
    const [dataMap, setDataMap] = useState({});
  
    useEffect(() => {
      const tickers = list.map(item => item.ticker).filter(Boolean);
      if (tickers.length === 0) return;
  
      fetchHistoricalDataBulk({ tickers, date_start, date_end }).then(raw => {
        const next = {};
        list.forEach(({ key, title, ticker }) => {
          if (!ticker) return;
          const d = raw[ticker];
          if (!d || d.data.length === 0) return;
  
          // A bar can legitimately carry no close: a holiday, or today before the
          // market has settled (DAS-2600 stores those as NULL instead of NaN). So
          // take the newest bar that HAS a price, not blindly the last one — reading
          // .close off the last bar crashed the whole panel on a null.
          const lastWithClose = [...d.data].reverse().find(pt => pt.close != null);
          const firstWithClose = d.data.find(pt => pt.close != null);
          if (!lastWithClose || !firstWithClose || !firstWithClose.close) return;
          const latest = lastWithClose.close;
          const first  = firstWithClose.close;
  
          next[key] = {
            title,
            value: latest.toLocaleString(),
            change: `${(((latest - first) / first) * 100).toFixed(2)}%`,
            data: d.data.map(pt => ({ value: pt.close })),
          };
        });
        setDataMap(next);
      });
    }, [list, date_start, date_end]);
  
    return dataMap;
  };

const WidgetGroup_RightPanel = () => {
  const { columns = [], rows = [] } = useSelector(
    state => state.portfolio?.data?.Sector_trend || {}
  );
  // DAS-2272: derive indices by name from the columns array so the component
  // keeps working under any future column-order change in the backend SELECT.
  const sectorIdx = columns.indexOf('sector');
  const trendIdx = columns.indexOf('trend_direction');
  const epochIdx = columns.indexOf('epoch');
  const explanationIdx = columns.indexOf('explanation');
  const fullReportIdx = columns.indexOf('full_report');
  const sectorReversalData = useSelector(
    state => state.portfolio?.data?.Sector_Reversal_Data || []
  );
  const sectorTrendPrevious = useSelector(
    state => state.portfolio?.data?.Sector_trend_previous || {}
  );
  const vectorsPrevious = useSelector(
    state => state.portfolio?.data?.Vectors_previous || {}
  );
  const vectors = useSelector(state => state.portfolio?.data?.vectors || []);
  const institutionalTrends = useSelector(state => state.portfolio?.data?.Vector_institutional_trend || {});
  const marketPositioningData = useSelector(
    state => state.portfolio?.data?.Market_Positioning_Analysis || {}
  );

  const menuRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [topStocksAnchorEl, setTopStocksAnchorEl] = useState(null);
  const [sectionSelectorAnchorEl, setSectionSelectorAnchorEl] = useState(null);
  // DAS-2565: Broad Picture (macro regime) is the default shown view in this panel.
  const [selectedSection, setSelectedSection] = useState('broadPicture'); // 'broadPicture', 'indicators', 'topStocks', 'sectorTrends'
  const [startDateOffset, setStartDateOffset] = useState(7); // default = 1 week
  const [topStocksDateOffset, setTopStocksDateOffset] = useState(7); // default = 1 week
  const [isHoveringMenu, setIsHoveringMenu] = useState(false);
  const [showAllPopup, setShowAllPopup] = useState(false);
  const [selectedSectorRow, setSelectedSectorRow] = useState(null);
  const [sectorModalOpen, setSectorModalOpen] = useState(false);
  const [selectedVector, setSelectedVector] = useState(null);
  const [marketAnalysisData, setMarketAnalysisData] = useState(null);
  const [marketModalOpen, setMarketModalOpen] = useState(false);
  const [selectedInstitutionalVector, setSelectedInstitutionalVector] = useState(null);
  const [institutionalAnalysisData, setInstitutionalAnalysisData] = useState(null);
  const [institutionalModalOpen, setInstitutionalModalOpen] = useState(false);
  const [sectorOverviewOpen, setSectorOverviewOpen] = useState(false);
  const [selectedMidTermItem, setSelectedMidTermItem] = useState(null);
  const [midTermAnalysisData, setMidTermAnalysisData] = useState(null);
  const [midTermModalOpen, setMidTermModalOpen] = useState(false);
  
  // Calculate required width based on content
  const calculateRequiredWidth = () => {
    let totalWidgets = 0;

    // DAS-2565: Broad Picture is a full prose/regime panel, not a widget grid —
    // give it the panel's full width (capped by the container's 800px maxWidth).
    if (selectedSection === 'broadPicture') {
      return 760;
    }

    if (selectedSection === 'sectorTrends') {
      totalWidgets = Math.min(rows.length, 16);
    } else if (selectedSection === 'marketStatus') {
      totalWidgets = Math.min(vectors.length, 16);
    } else if (selectedSection === 'institutionalTrends') {
      const { columns = [] } = institutionalTrends;
      totalWidgets = Math.max(0, columns.length - 2); // Subtract ID and epoch columns
    } else if (selectedSection === 'institutionalMidTerm') {
      // Up to 6 widgets: top 3 strong + top 3 weak (if available)
      const { columns = [], rows = [] } = marketPositioningData || {};
      let count = 0;
      if (rows && rows[0] && columns && columns.length) {
        try {
          const fullResultJson = rows[0][columns.indexOf('full_result_json')];
          const jsonData = typeof fullResultJson === 'string' ? JSON.parse(fullResultJson) : fullResultJson;
          const top5Strong = (jsonData && jsonData.top5_strong) || [];
          const top5Weak = (jsonData && jsonData.top5_weak) || [];
          count = Math.min(3, top5Strong.length) + Math.min(3, top5Weak.length);
        } catch (e) {
          count = 0;
        }
      }
      totalWidgets = count;
    } else {
      // indicators and topStocks
      totalWidgets = selectedSection === 'indicators' ? tickers.length : tickers2.length;
    }
    
    const defaultColumns = 4;
    const maxRows = 10;
    
    let optimalColumns = defaultColumns;
    let optimalRows = Math.ceil(totalWidgets / optimalColumns);
    
    if (optimalRows > maxRows) {
      optimalColumns = Math.ceil(totalWidgets / maxRows);
      optimalColumns = Math.min(optimalColumns, 8);
    }
    
    return optimalColumns * 100 + 32; // 100px per column + padding
  };
  
  const requiredWidth = calculateRequiredWidth();

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  const handleSettingsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTopStocksSettingsClick = (event) => {
    setTopStocksAnchorEl(event.currentTarget);
  };

  const handleSectionSelectorMouseEnter = (event) => {
    setSectionSelectorAnchorEl(event.currentTarget);
  };

  const handleSectionSelectorMouseLeave = () => {
    // Clear any existing timeout
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    
    dropdownTimeoutRef.current = setTimeout(() => {
      if (!isHoveringMenu) {
        setSectionSelectorAnchorEl(null);
      }
    }, 300); // Increased from 100ms to 300ms for better user experience
  };

  const handleSectionMenuMouseEnter = () => {
    // Clear any existing timeout when entering menu
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsHoveringMenu(true);
  };

  const handleSectionMenuMouseLeave = () => {
    setIsHoveringMenu(false);
    
    // Clear any existing timeout
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    
    dropdownTimeoutRef.current = setTimeout(() => {
      setSectionSelectorAnchorEl(null);
    }, 200); // Added delay to prevent immediate closing
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTopStocksClose = () => {
    setTopStocksAnchorEl(null);
  };

  const handleSectorWidgetClick = (row) => {
    setSelectedSectorRow(row);
    setSectorModalOpen(true);
  };

  const handleMarketWidgetClick = async (vectorName) => {
    setSelectedVector(vectorName);
    setMarketModalOpen(true);
    
    try {
      let analysis_type = vectorName.startsWith("Institutional_trend")
        ? "Institutional_trend" : vectorName;

      // Tolerant mapping for EPS-related labels to Fundamental analysis
      // DAS-2310: also match new contrarian-momentum labels
      const nameLower = (vectorName || "").toLowerCase();
      if (
        nameLower.includes("eps surprises") ||
        nameLower.includes("eps growth") ||
        nameLower.includes("eps expansion") ||
        nameLower.includes("eps contraction") ||
        nameLower.includes("eps direction")
      ) {
        analysis_type = "Fundamental";
      }

      const response = await api.post("/Market_analysis", { analysis_type });
      setMarketAnalysisData(response.data);
    } catch (error) {
      setMarketAnalysisData({ error: "Failed to retrieve analysis" });
    }
  };

  const handleInstitutionalWidgetClick = async (columnName) => {
    setSelectedInstitutionalVector(columnName);
    setInstitutionalModalOpen(true);
    
    try {
      const analysis_type = "Institutional_trend";
      const response = await api.post("/Market_analysis", { analysis_type });
      setInstitutionalAnalysisData(response.data);
    } catch (error) {
      setInstitutionalAnalysisData({ error: "Failed to retrieve analysis" });
    }
  };

  const handleMidTermWidgetClick = async (itemLabel) => {
    setSelectedMidTermItem(itemLabel);
    setMidTermModalOpen(true);
    try {
      const analysis_type = "Institutional_trend_mid_term";
      const response = await api.post("/Market_analysis", { analysis_type });
      setMidTermAnalysisData(response.data);
    } catch (error) {
      setMidTermAnalysisData({ error: "Failed to retrieve analysis" });
    }
  };

  const handleSectionChange = (section) => {
    if (section === 'showAll') {
      setShowAllPopup(true);
      setIsHoveringMenu(false);
      setSectionSelectorAnchorEl(null);
    } else {
      setSelectedSection(section);
      setIsHoveringMenu(false);
      setSectionSelectorAnchorEl(null);
    }
  };

  const handleDateChange = (offset) => {
    setStartDateOffset(offset);
    handleClose();
  };

  const handleTopStocksDateChange = (offset) => {
    setTopStocksDateOffset(offset);
    handleTopStocksClose();
  };

  const date_start = getDateDaysAgo(startDateOffset);
  const date_end = new Date().toISOString().split('T')[0];
  const topStocksDate_start = getDateDaysAgo(topStocksDateOffset);
  const topStocksDate_end = new Date().toISOString().split('T')[0];

  const group1Data = useWidgetData(tickers, date_start, date_end);
  const group2Data = useWidgetData(tickers2, topStocksDate_start, topStocksDate_end);

  const getReversalIndicator = (sectorName) => {
    const sectorData = sectorReversalData.find(item => item.sector === sectorName);
    if (!sectorData || !sectorData.final_reversal_strength) return null;
    
    const strength = sectorData.final_reversal_strength;
    if (strength.includes('NO REVERSAL')) return null;
    
    if (strength.includes('BEARISH')) return '#FF8C00'; // Orange
    if (strength.includes('BULLISH')) return '#90EE90'; // Light green
    
    return '#FF8C00'; // Default to orange for other reversal types
  };

  const getSectorTrendColor = (trendDirection) => {
    if (typeof trendDirection !== 'string') return 'transparent';
    
    const trend = trendDirection.toLowerCase();
    if (trend.includes('strong') && trend.includes('bullish')) return '#DFF2BF'; // Light green
    if (trend.includes('moderate') && trend.includes('bullish')) return '#E6F5D0'; // Lighter green
    if (trend.includes('mild') && trend.includes('bullish')) return '#F0F8F0'; // Very light green
    if (trend.includes('neutral')) return '#F5F5F5'; // Light gray
    if (trend.includes('mild') && trend.includes('bearish')) return '#FFF8E1'; // Light yellow
    if (trend.includes('moderate') && trend.includes('bearish')) return '#FFE6B3'; // Light orange
    if (trend.includes('strong') && trend.includes('bearish')) return '#FFB3B3'; // Light red
    if (trend.includes('bearish')) return '#FEEFB3'; // Fallback for generic bearish
    
    return 'transparent';
  };

  const getTrendChangeIndicator = (sectorName, currentTrend) => {
    const previousData = sectorTrendPrevious[sectorName];
    if (!previousData) return null;
    
    const previousTrend = previousData.trend_direction;
    if (previousTrend === currentTrend) return null; // No change
    
    // Determine change direction
    const isBullish = (trend) => trend.toLowerCase().includes('bullish');
    const isBearish = (trend) => trend.toLowerCase().includes('bearish');
    
    if (isBullish(currentTrend) && isBearish(previousTrend)) {
      return { type: 'improved', color: '#90EE90', symbol: '↗' }; // Green up arrow
    } else if (isBearish(currentTrend) && isBullish(previousTrend)) {
      return { type: 'worsened', color: '#FF6B6B', symbol: '↘' }; // Red down arrow
    } else if (isBullish(currentTrend) && !isBullish(previousTrend) && !isBearish(previousTrend)) {
      return { type: 'improved', color: '#90EE90', symbol: '↗' }; // Green up arrow
    } else if (isBearish(currentTrend) && !isBearish(previousTrend) && !isBullish(previousTrend)) {
      return { type: 'worsened', color: '#FF6B6B', symbol: '↘' }; // Red down arrow
    }
    
    return { type: 'changed', color: '#FFD700', symbol: '↔' }; // Yellow neutral arrow
  };

  const getTrendComparisonText = (sectorName, currentTrend) => {
    const previousData = sectorTrendPrevious[sectorName];
    if (!previousData) return null;
    
    const previousTrend = previousData.trend_direction;
    if (previousTrend === currentTrend) return null;
    
    return `vs ${previousTrend}`;
  };

  const getVectorChangeIndicator = (vectorName, currentValue) => {
    const previousData = vectorsPrevious[vectorName];
    if (!previousData) return null;
    
    const previousValue = previousData.Vector_value;
    if (previousValue === currentValue) return null; // No change
    
    // Determine change direction based on market sentiment
    const isBullish = (value) => value.toLowerCase().includes('bullish') || 
                               value.toLowerCase().includes('strong') || 
                               value.toLowerCase().includes('no panic') ||
                               value.toLowerCase().includes('stable');
    
    const isBearish = (value) => value.toLowerCase().includes('bearish') || 
                                value.toLowerCase().includes('weak') || 
                                value.toLowerCase().includes('panic') ||
                                value.toLowerCase().includes('stress') ||
                                value.toLowerCase().includes('drop risk');
    
    if (isBullish(currentValue) && isBearish(previousValue)) {
      return { type: 'improved', color: '#90EE90', symbol: '↗', tooltip: `up from previously "${previousValue}"` };
    } else if (isBearish(currentValue) && isBullish(previousValue)) {
      return { type: 'worsened', color: '#FF6B6B', symbol: '↘', tooltip: `down from previously "${previousValue}"` };
    } else if (isBullish(currentValue) && !isBullish(previousValue) && !isBearish(previousValue)) {
      return { type: 'improved', color: '#90EE90', symbol: '↗', tooltip: `up from previously "${previousValue}"` };
    } else if (isBearish(currentValue) && !isBearish(previousValue) && !isBullish(previousValue)) {
      return { type: 'worsened', color: '#FF6B6B', symbol: '↘', tooltip: `down from previously "${previousValue}"` };
    }
    
    return { type: 'changed', color: '#FFD700', symbol: '↔', tooltip: `changed from previously "${previousValue}"` };
  };

  const checkAllInstitutionalTrendsNeutral = () => {
    const { columns = [], rows = [] } = institutionalTrends;
    const row = rows[0]; // Take the first (most recent) row
    
    if (!row || columns.length <= 2) return false;
    
    // Check if all institutional trend signals are 'Neutral'
    const allNeutral = columns.slice(2).every((column, idx) => {
      const signal = row[idx + 2]; // +2 to account for ID and epoch columns
      return signal === 'Neutral';
    });
    
    return allNeutral;
  };

  const getMarketStatusColor = (value) => {
    if (typeof value !== 'string') return 'transparent';
    
    const status = value.toLowerCase();
    if (status.includes('no panic') || status.includes('stable') || status.includes('bullish') ||
        status.includes('strong') || status.includes('strengthening') || status.includes('market has strong support')) {
      return '#DFF2BF'; // Green
    }
    
    if (status.includes('weakening') || status.includes('moderate drop risk') || status.includes('caution') ||
        status.includes('no clear') || status.includes('inconclusive') || status.includes('equilibrium')) {
      return '#FEEFB3'; // Orange
    }
    
    if (status.includes('bearish') || status.includes('weak') || status.includes('panic behaviour') ||
        status.includes('stress') || status.includes('deep drop risk')) {
      return '#FFB3B3'; // Light red
    }
    
    return 'transparent';
  };

  const abbreviateMarketStatus = (status) => {
    if (typeof status !== 'string') return status;
    
    const statusLower = status.toLowerCase();
    
    // Key term mappings for common market status phrases
    const abbreviations = {
      'bullish reversal': 'Bullish',
      'bearish reversal': 'Bearish',
      'no panic': 'No Panic',
      'mixed - unclear': 'Mixed',
      'low - market has strong support': 'Low Support',
      'stable macro': 'Stable',
      'selling urgency': 'Sell Urgency',
      'buying urgency': 'Buy Urgency',
      'risk-off': 'Risk-Off',
      'risk-on': 'Risk-On',
      'strong buy': 'STRONG BUY',
      'accumulate': 'ACCUMULATE',
      'eps surprises market analysis': 'EPS Analysis',
      // DAS-2310: EPS-momentum contrarian labels (new). Direction is contrarian:
      // "Broad Growth" = late-cycle bearish, "Healthy Expansion" = sweet spot bullish.
      'broad eps growth – late cycle': 'EPS Froth ⚠️',
      'healthy eps expansion': 'EPS Healthy 📈',
      'moderate eps expansion': 'EPS Moderate',
      'mixed eps direction': 'EPS Mixed',
      'broad eps contraction': 'EPS Contraction',
      'weak': 'Weak',
      'moderate': 'Moderate',
      'strong': 'Strong',
      'weakening': 'Weakening',
      'strengthening': 'Strengthening',
      'caution': 'Caution',
      'stress': 'Stress',
      'panic': 'Panic',
      'stable': 'Stable',
      'unclear': 'Unclear',
      'inconclusive': 'Inconclusive',
      'equilibrium': 'Equilibrium'
    };
    
    // Try to find exact matches first
    for (const [phrase, abbrev] of Object.entries(abbreviations)) {
      if (statusLower.includes(phrase)) {
        return abbrev;
      }
    }
    
    // If no exact match, try to extract key words
    const keyWords = ['bullish', 'bearish', 'strong', 'weak', 'stable', 'panic', 'stress', 'risk', 'buy', 'sell'];
    const foundWords = keyWords.filter(word => statusLower.includes(word));
    
    if (foundWords.length > 0) {
      return foundWords.slice(0, 2).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
    
    // If still no match, truncate to first 15 characters
    return status.length > 15 ? status.substring(0, 15) + '...' : status;
  };

  const getInstitutionalTrendColor = (signal) => {
    if (typeof signal !== 'string') return 'transparent';
    
    const signalLower = signal.toLowerCase();
    if (signalLower.includes('strong buy')) return '#DFF2BF'; // Light green
    if (signalLower.includes('weak buy')) return '#E6F5D0'; // Lighter green
    if (signalLower.includes('neutral')) return '#F5F5F5'; // Light gray
    if (signalLower.includes('weak sell')) return '#FFE6B3'; // Light orange
    if (signalLower.includes('strong sell')) return '#FFB3B3'; // Light red
    
    return 'transparent';
  };

  const getMidTermSignalColor = (value) => {
    if (typeof value !== 'string') return 'transparent';
    if (value.includes('Strong Buying')) return '#DFF2BF';
    if (value.includes('Weak Buying')) return '#E6F5D0';
    if (value.includes('Weak Selling')) return '#FEEFB3';
    if (value.includes('Strong Selling')) return '#FFB3B3';
    return 'transparent';
  };

  const formatInstitutionalTrendName = (columnName) => {
    if (!columnName) return '';
    
    // Remove the "Institutional_trend_" prefix
    let name = columnName.replace(/^Institutional_trend_/, '');
    
    // Split by underscore and format
    const parts = name.split('_');
    if (parts.length >= 2) {
      const region = parts[0];
      const sector = parts.slice(1).join(' ');
      
      // Format region names
      const regionMap = {
        'Global': 'Global',
        'USA': 'US',
        'Europe': 'EU',
        'Japan': 'JP',
        'Taiwan': 'TW',
        'China': 'CN'
      };
      
      const formattedRegion = regionMap[region] || region;
      return `${formattedRegion} ${sector}`;
    }
    
    return name;
  };

  const renderGrid = (dataObj, group, sectionOverride = null) => {
    let widgets = [];
    const currentSection = sectionOverride || selectedSection;
    
    if (currentSection === 'sectorTrends') {
      // Create sector trend widgets
      widgets = rows.slice(0, 16).map((row, idx) => {
        const sectorName = row[sectorIdx];
        const trendDirection = row[trendIdx];
        const indicatorColor = getReversalIndicator(sectorName);
        const backgroundColor = getSectorTrendColor(trendDirection);
        
        return {
          key: `sector-${idx}`,
          component: (
            <Box 
              sx={{ 
                width: '100%', 
                height: '100%', 
                p: 0.5,
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => handleSectorWidgetClick(row)}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.7rem', 
                  fontWeight: 'bold',
                  display: 'block',
                  lineHeight: 1.2
                }}
              >
                {sectorName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.6rem',
                    lineHeight: 1.2
                  }}
                >
                  {trendDirection}
                </Typography>
                {indicatorColor && (
                  <Box
                    sx={{
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      backgroundColor: indicatorColor,
                      flexShrink: 0
                    }}
                  />
                )}
                {(() => {
                  const changeIndicator = getTrendChangeIndicator(sectorName, trendDirection);
                  return changeIndicator && (
                    <Typography
                      sx={{
                        fontSize: '0.5rem',
                        color: changeIndicator.color,
                        fontWeight: 'bold',
                        ml: 0.2
                      }}
                    >
                      {changeIndicator.symbol}
                    </Typography>
                  );
                })()}
              </Box>
              {(() => {
                const comparisonText = getTrendComparisonText(sectorName, trendDirection);
                return comparisonText && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.5rem', 
                      color: '#666',
                      fontStyle: 'italic',
                      mt: 0.2
                    }}
                  >
                    {comparisonText}
                  </Typography>
                );
              })()}
            </Box>
          ),
          backgroundColor: backgroundColor
        };
      });
    } else if (currentSection === 'marketStatus') {
      // Create market status widgets
      widgets = vectors.slice(0, 16).map((vector, idx) => {
        const vectorName = vector[1];
        const vectorStatus = vector[2];
        const abbreviatedStatus = abbreviateMarketStatus(vectorStatus);
        const backgroundColor = getMarketStatusColor(vectorStatus);
        
        return {
          key: `market-${idx}`,
          component: (
            <Box 
              sx={{ 
                width: '100%', 
                height: '100%', 
                p: 0.5,
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => handleMarketWidgetClick(vectorName)}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.7rem', 
                  fontWeight: 'bold',
                  display: 'block',
                  lineHeight: 1.2
                }}
              >
                {vectorName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.6rem',
                    lineHeight: 1.2
                  }}
                >
                  {abbreviatedStatus}
                </Typography>
                {(() => {
                  const changeIndicator = getVectorChangeIndicator(vectorName, vectorStatus);
                  return changeIndicator && (
                    <Typography
                      title={changeIndicator.tooltip}
                      sx={{
                        fontSize: '0.8rem',
                        color: changeIndicator.color,
                        fontWeight: 'bold',
                        ml: 0.3,
                        cursor: 'help',
                        textShadow: '0 0 2px rgba(0,0,0,0.3)'
                      }}
                    >
                      {changeIndicator.symbol}
                    </Typography>
                  );
                })()}
              </Box>
            </Box>
          ),
          backgroundColor: backgroundColor
        };
      });
    } else if (currentSection === 'institutionalTrends') {
      // Create institutional trend widgets
      const { columns = [], rows = [] } = institutionalTrends;
      const row = rows[0]; // Take the first (most recent) row
      
      if (row && columns.length > 2) {
        widgets = columns.slice(2).map((column, idx) => { // Skip ID and epoch, take all remaining columns
          const signal = row[idx + 2]; // +2 to account for ID and epoch columns
          const backgroundColor = getInstitutionalTrendColor(signal);
          const formattedName = formatInstitutionalTrendName(column);
          
          return {
            key: `institutional-${idx}`,
            component: (
              <Box 
                sx={{ 
                  width: '100%', 
                  height: '100%', 
                  p: 0.5,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 }
                }}
                onClick={() => handleInstitutionalWidgetClick(column)}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.7rem', 
                    fontWeight: 'bold',
                    display: 'block',
                    lineHeight: 1.2
                  }}
                >
                  {formattedName}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.6rem',
                    lineHeight: '1.2',
                    mt: 0.5
                  }}
                >
                  {signal}
                </Typography>
              </Box>
            ),
            backgroundColor: backgroundColor
          };
        });
      }
    } else if (currentSection === 'institutionalMidTerm') {
      // Create mid-term institutional widgets from Market_Positioning_Analysis
      const { columns = [], rows = [] } = marketPositioningData || {};
      if (rows && rows[0] && columns && columns.length) {
        try {
          const fullResultJson = rows[0][columns.indexOf('full_result_json')];
          const jsonData = typeof fullResultJson === 'string' ? JSON.parse(fullResultJson) : fullResultJson;
          const top5Strong = (jsonData && jsonData.top5_strong) || [];
          const top5Weak = (jsonData && jsonData.top5_weak) || [];
          const strongItems = top5Strong.slice(0, 3).map((item, idx) => ({ ...item, __cat: 'Strong', __idx: idx }));
          const weakItems = top5Weak.slice(0, 3).map((item, idx) => ({ ...item, __cat: 'Weak', __idx: idx }));
          const items = [...strongItems, ...weakItems];

          widgets = items.map((item, idx) => {
            const label = item.region && item.sector ? `${item.region} ${item.sector}` : (item.sector || item.region || item.net || 'Unknown');
            const backgroundColor = getMidTermSignalColor(item.pick || '');
            return {
              key: `institutional-mid-${idx}`,
              component: (
                <Box 
                  sx={{ 
                    width: '100%', 
                    height: '100%', 
                    p: 0.5,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 }
                  }}
                  onClick={() => handleMidTermWidgetClick(label)}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.7rem', 
                      fontWeight: 'bold',
                      display: 'block',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                    title={label}
                  >
                    {label}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.6rem',
                      lineHeight: '1.2',
                      mt: 0.5,
                      fontWeight: 'bold'
                    }}
                  >
                    {item.pick}
                  </Typography>
                </Box>
              ),
              backgroundColor
            };
          });
        } catch (e) {
          widgets = [];
        }
      }
    } else {
      // Original logic for indicators and top stocks
      widgets = group.map(({ key, title, ticker, component }) => {
        if (component) {
          return { key, component };
        }
        const d = dataObj[key];
        return {
          key,
          component: d
            ? <Chart_Small_MUI title={d.title} value={d.value} change={d.change} data={d.data} />
            : <Typography fontSize="0.6rem" color="textSecondary">Loading…</Typography>
        };
      });
    }

    // Calculate optimal grid layout
    const totalWidgets = widgets.length;
    const defaultColumns = 4;
    const maxRows = 10; // Maximum rows we want to show
    
    // Start with default 4 columns
    let optimalColumns = defaultColumns;
    let optimalRows = Math.ceil(totalWidgets / optimalColumns);
    
    // Only expand columns if we have too many rows (more than maxRows)
    if (optimalRows > maxRows) {
      optimalColumns = Math.ceil(totalWidgets / maxRows);
      optimalColumns = Math.min(optimalColumns, 8); // Maximum 8 columns
      optimalRows = Math.ceil(totalWidgets / optimalColumns);
    }
    
    // Fill empty widgets to complete the grid
    while (widgets.length < optimalColumns * optimalRows) {
      widgets.push({ key: `empty-${widgets.length}`, component: null });
    }

    return (
      <Box
        display="grid"
        gridTemplateColumns={`repeat(${optimalColumns}, 100px)`}
        gridTemplateRows={`repeat(${optimalRows}, 50px)`}
        gap={0}
        justifyItems="center"
        alignItems="center"
        sx={{
          borderCollapse: 'collapse',
          [`& > *:not(:nth-child(${optimalColumns}n))`]: {
            borderRight: '1px dotted #ccc',
          },
          [`& > *:nth-child(n + ${optimalColumns + 1})`]: {
            borderTop: '1px dotted #ccc',
          },
        }}
      >
        {widgets.map(({ key, component, backgroundColor }) => (
          <Box
            key={key}
            sx={{
              width: '100px',
              height: '50px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden', // ✅ ensures no child content spills outside

            }}
          >
            <Paper
  elevation={1}
  sx={{
    flex: 1,
    p: 0.5,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: backgroundColor || 'transparent',
    transition: 'box-shadow 0.3s ease, transform 0.2s ease',
    '&:hover': {
      boxShadow: 3,
      transform: 'translateY(-2px)',
    },
  }}
>
  {component}
</Paper>

          </Box>
        ))}
      </Box>
    );
  };

  const getOffsetLabel = (offset) => {
    return {
      1: '1 d',
      7: '1 wk',
      14: '2 wk',
      30: '1 mo',
    }[offset] || `${offset} d`;
  };

  const indicatorsOffsetLabel = getOffsetLabel(startDateOffset);
  const topStocksOffsetLabel = getOffsetLabel(topStocksDateOffset);
  

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={0.5}
      minHeight="400px"
      sx={{
        overflowY: 'auto',
        // DAS-2566: never produce a horizontal scrollbar. Without an explicit
        // overflowX, the browser computes overflow-x:auto from overflowY:auto and
        // shows a sideways bar on any overflow.
        overflowX: 'hidden',
        width: `${requiredWidth}px`,
        maxWidth: '100%',
        '@media (min-width: 1000px)': {
          overflowY: 'visible',
          overflowX: 'visible',
        },
      }}
    >
      {/* Top divider with section selector and settings */}
      <Box
        display="flex"
        alignItems="center"
        sx={{ width: '100%', mb: 0.1, position: 'relative' }}
      >
        <Divider sx={{ flexGrow: 1 }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mx: 1,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
          onMouseEnter={handleSectionSelectorMouseEnter}
          onMouseLeave={handleSectionSelectorMouseLeave}
        >
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {selectedSection === 'broadPicture' && 'Broad Picture'}
            {selectedSection === 'indicators' && `Indicators (${indicatorsOffsetLabel})`}
            {selectedSection === 'topStocks' && `Top Stocks (${topStocksOffsetLabel})`}
            {selectedSection === 'sectorTrends' && 'Sector Trends'}
            {selectedSection === 'marketStatus' && 'Marketstatus'}
            {selectedSection === 'institutionalTrends' && 'Institutional Trend'}
            {selectedSection === 'institutionalMidTerm' && 'Institutional Mid Term'}
            {selectedSection === 'showAll' && 'Show All'}
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ 
              ml: 0.5,
              transition: 'transform 0.2s ease',
              transform: Boolean(sectionSelectorAnchorEl) ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          >
            ▼
          </Typography>
        </Box>
        {selectedSection === 'indicators' && (
          <SettingsIcon
            fontSize="small"
            sx={{ color: 'text.secondary', cursor: 'pointer' }}
            onClick={handleSettingsClick}
          />
        )}
        {selectedSection === 'topStocks' && (
          <SettingsIcon
            fontSize="small"
            sx={{ color: 'text.secondary', cursor: 'pointer' }}
            onClick={handleTopStocksSettingsClick}
          />
        )}
        <Divider sx={{ flexGrow: 1, ml: 1 }} />
        
        {/* Section Selector Dropdown */}
        {Boolean(sectionSelectorAnchorEl) && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1300,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: 1,
              boxShadow: 2,
              minWidth: 120,
              mt: 0, // Removed margin top to eliminate gap
              pt: 0.5 // Added padding top instead
            }}
            onMouseEnter={handleSectionMenuMouseEnter}
            onMouseLeave={handleSectionMenuMouseLeave}
          >
            {/* Invisible bridge to prevent gap */}
            <Box
              sx={{
                position: 'absolute',
                top: '-4px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100px',
                height: '4px',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={handleSectionMenuMouseEnter}
            />
            {[
              { key: 'broadPicture', label: 'Broad Picture' },
              { key: 'indicators', label: 'Indicators' },
              { key: 'topStocks', label: 'Top Stocks' },
              { key: 'sectorTrends', label: 'Sector Trends' },
              { key: 'marketStatus', label: 'Marketstatus' },
              { key: 'institutionalTrends', label: 'Institutional Trend' },
              { key: 'institutionalMidTerm', label: 'Institutional Mid Term' },
              { key: 'showAll', label: 'Show All' }
            ].map((section) => (
              <Box
                key={section.key}
                onClick={() => handleSectionChange(section.key)}
                sx={{ 
                  px: 2,
                  py: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)'
                  },
                  backgroundColor: selectedSection === section.key ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                }}
              >
                <Typography variant="body2">
                  {section.label}
                </Typography>
                {selectedSection === section.key && (
                  <Typography variant="body2" color="primary">
                    ✓
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Indicators Settings Popover */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box>
            {[1, 7, 14, 30].map((d) => (
              <MenuItem
                key={d}
                selected={startDateOffset === d}
                onClick={() => handleDateChange(d)}
                sx={{ justifyContent: 'space-between' }}
              >
                {d === 1
                  ? '1 day ago'
                  : d === 7
                  ? '1 week ago'
                  : d === 14
                  ? '2 weeks ago'
                  : '1 month ago'}
                {startDateOffset === d && (
                  <Typography variant="body2" color="primary">
                    ✓
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Box>
        </Popover>

        {/* Top Stocks Settings Popover */}
        <Popover
          open={Boolean(topStocksAnchorEl)}
          anchorEl={topStocksAnchorEl}
          onClose={handleTopStocksClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box>
            {[1, 7, 14, 30].map((d) => (
              <MenuItem
                key={d}
                selected={topStocksDateOffset === d}
                onClick={() => handleTopStocksDateChange(d)}
                sx={{ justifyContent: 'space-between' }}
              >
                {d === 1
                  ? '1 day ago'
                  : d === 7
                  ? '1 week ago'
                  : d === 14
                  ? '2 weeks ago'
                  : '1 month ago'}
                {topStocksDateOffset === d && (
                  <Typography variant="body2" color="primary">
                    ✓
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Box>
        </Popover>
      </Box>

      {/* Single grid that changes based on selected section */}
      <Box flex="1 1 auto">
        {/* DAS-2565: Broad Picture (macro regime) — the default view. Renders the
            full read-only panel (latest run, axes, narrative, tailwinds/headwinds,
            sources, cost) inline; same component used by Tools → Broad Picture.
            DAS-2566: clip horizontal overflow so the panel never needs a sideways bar. */}
        {selectedSection === 'broadPicture' && (
          <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
            {/* DAS-2635: two-way cross-link — jump straight to the Market Status grid. */}
            <BroadPicture onOpenMarketStatus={() => setSelectedSection('marketStatus')} />
          </Box>
        )}
        {selectedSection === 'indicators' && renderGrid(group1Data, tickers, 'indicators')}
        {selectedSection === 'topStocks' && renderGrid(group2Data, tickers2, 'topStocks')}
        {selectedSection === 'sectorTrends' && renderGrid([], [], 'sectorTrends')}
        {selectedSection === 'marketStatus' && (
          <Box>
            {/* DAS-2635: reverse cross-link to the top-down Broad Picture summary. */}
            <Typography
              variant="caption"
              onClick={() => setSelectedSection('broadPicture')}
              sx={{ color: 'primary.main', cursor: 'pointer', display: 'block', mb: 1 }}
            >
              → See the Broad Picture (top-down summary of these signals)
            </Typography>
            {renderGrid([], [], 'marketStatus')}
          </Box>
        )}
        {selectedSection === 'institutionalTrends' && renderGrid([], [], 'institutionalTrends')}
        {selectedSection === 'institutionalMidTerm' && renderGrid([], [], 'institutionalMidTerm')}
        {selectedSection === 'institutionalMidTerm' && renderGrid([], [])}
      </Box>

      {/* Show All Popup */}
      <Dialog
        open={showAllPopup}
        onClose={() => setShowAllPopup(false)}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '95vh',
            overflowY: 'auto',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          borderRadius: '16px 16px 0 0'
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              📊 Complete Market Overview
            </Typography>
            <IconButton 
              onClick={() => setShowAllPopup(false)}
              sx={{ 
                backgroundColor: '#f1f3f4',
                '&:hover': { backgroundColor: '#e8eaed' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" flexDirection="column" gap={4}>

            {/* Sector Overview */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 'bold', 
                color: '#2c3e50',
                borderBottom: '2px solid #34495e',
                pb: 1,
                mb: 2,
                display: 'flex',
                alignItems: 'center'
              }}>
                📚 Sector Overview
                <InfoTooltip 
                  text="Consolidated sector view: 30d weighted trend, institutional flows (short & mid-term) by region, and Trendsearch sector/industry sentiment."
                  placement="top"
                />
              </Typography>
              <Paper elevation={2} sx={{ p: 2, borderRadius: '12px' }}>
                <SectorOverview inline />
              </Paper>
            </Box>

            {/* Market Status */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 'bold', 
                color: '#2c3e50',
                borderBottom: '2px solid #f39c12',
                pb: 1,
                mb: 2,
                display: 'flex',
                alignItems: 'center'
              }}>
                📊 Market Status
                <InfoTooltip 
                  text="Multi-layered market health analysis combining institutional flows (A/D Line, OBV), market breadth (advance-decline, % above 50MA), sector leadership (XLF, XLI vs S&P 500), and panic indicators (VIX, put/call, volume)."
                  placement="top"
                />
              </Typography>
              <Paper elevation={2} sx={{ p: 2, borderRadius: '12px' }}>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(4, 1fr)', 
                  gap: 1
                }}>
                  {renderGrid([], [], 'marketStatus')}
                </Box>
              </Paper>
            </Box>

            {/* Indicators */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 'bold', 
                color: '#2c3e50',
                borderBottom: '2px solid #3498db',
                pb: 1,
                mb: 2
              }}>
                📈 Indicators ({indicatorsOffsetLabel})
              </Typography>
              <Paper elevation={2} sx={{ p: 2, borderRadius: '12px' }}>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(4, 1fr)', 
                  gap: 1
                }}>
                  {renderGrid(group1Data, tickers, 'indicators')}
                </Box>
              </Paper>
            </Box>

            {/* Top Stocks */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 'bold', 
                color: '#2c3e50',
                borderBottom: '2px solid #e74c3c',
                pb: 1,
                mb: 2
              }}>
                🏢 Top Stocks ({topStocksOffsetLabel})
              </Typography>
              <Paper elevation={2} sx={{ p: 2, borderRadius: '12px' }}>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(4, 1fr)', 
                  gap: 1
                }}>
                  {renderGrid(group2Data, tickers2, 'topStocks')}
                </Box>
              </Paper>
            </Box>

            {/* Institutional by Region (Short & Mid) */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 'bold', 
                color: '#2c3e50',
                borderBottom: '2px solid #9b59b6',
                pb: 1,
                mb: 2,
                display: 'flex',
                alignItems: 'center'
              }}>
                🏛️ Institutional by Region (Short & Mid)
                <InfoTooltip 
                  text="Short-term flow signals and mid-term positioning combined by region across sectors."
                  placement="top"
                />
              </Typography>
              <InstitutionalRegionOverview />
            </Box>

          </Box>
        </DialogContent>
      </Dialog>

      {/* Sector Trends Full Report Modal */}
      <Dialog open={sectorModalOpen} onClose={() => setSectorModalOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontSize: '16px' }}>
          {selectedSectorRow?.[sectorIdx] || 'Sector'} - Full Report
          {selectedSectorRow?.[epochIdx] && (
            <span style={{ fontWeight: 'normal', fontSize: '13px', marginLeft: '10px', color: '#888' }}>
              ({epochToDate(selectedSectorRow[epochIdx])})
            </span>
          )}
        </DialogTitle>
        <DialogContent sx={{ whiteSpace: 'pre-wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Summary:
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedSectorRow?.[explanationIdx] || '(no summary)'}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Full Report:
          </Typography>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
            {selectedSectorRow?.[fullReportIdx] || '(no full report)'}
          </ReactMarkdown>
        </DialogContent>
      </Dialog>

      {/* Market Status Analysis Modal */}
      <MarketAnalysisModal
        open={marketModalOpen}
        onClose={() => setMarketModalOpen(false)}
        selectedVector={selectedVector}
        analysisData={marketAnalysisData}
      />

      {/* Institutional Trend Analysis Modal */}
      <Dialog
        open={institutionalModalOpen}
        onClose={() => setInstitutionalModalOpen(false)}
        fullWidth
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "90vw",
            maxWidth: "1200px",
            minWidth: "300px",
            overflowX: "hidden"
          }
        }}
      >
        <DialogTitle>{selectedInstitutionalVector} Analysis</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
            width: "100%",
            padding: "16px"
          }}
        >
          {institutionalAnalysisData ? (
            <Box sx={{ width: "100%" }}>
              {institutionalAnalysisData.purpose && (
                <>
                  <Typography variant="h6" gutterBottom><strong>Result:</strong></Typography>
                  <Box sx={{
                    display: "block",
                    width: "100%",
                    maxWidth: "100%",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                    lineBreak: "anywhere"
                  }}>
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                      {institutionalAnalysisData.result || "N/A"}
                    </ReactMarkdown>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              <Typography variant="h6" gutterBottom><strong>Purpose:</strong></Typography>
              <Box sx={{
                display: "block",
                width: "100%",
                maxWidth: "100%",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                lineBreak: "anywhere"
              }}>
                <ReactMarkdown>{institutionalAnalysisData.purpose}</ReactMarkdown>
              </Box>

              <Divider sx={{ my: 2 }} />
            </Box>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Sector Overview Modal */}
      <SectorOverview open={sectorOverviewOpen} onClose={() => setSectorOverviewOpen(false)} />

      {/* Institutional Mid Term Analysis Modal */}
      <Dialog
        open={midTermModalOpen}
        onClose={() => setMidTermModalOpen(false)}
        fullWidth
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "90vw",
            maxWidth: "1200px",
            minWidth: "300px",
            overflowX: "hidden"
          }
        }}
      >
        <DialogTitle>{selectedMidTermItem} Analysis (Mid Term)</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
            width: "100%",
            padding: "16px"
          }}
        >
          {midTermAnalysisData ? (
            <Box sx={{ width: "100%" }}>
              {midTermAnalysisData.result && (
                <>
                  <Typography variant="h6" gutterBottom><strong>Result:</strong></Typography>
                  <Box sx={{
                    display: "block",
                    width: "100%",
                    maxWidth: "100%",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                    lineBreak: "anywhere"
                  }}>
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                      {midTermAnalysisData.result || "N/A"}
                    </ReactMarkdown>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              <Typography variant="h6" gutterBottom><strong>Purpose:</strong></Typography>
              <Box sx={{
                display: "block",
                width: "100%",
                maxWidth: "100%",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                lineBreak: "anywhere"
              }}>
                <ReactMarkdown>{midTermAnalysisData.purpose}</ReactMarkdown>
              </Box>

              <Divider sx={{ my: 2 }} />
            </Box>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
  
};

export default WidgetGroup_RightPanel;
